import React from 'react';
import dayjs from 'dayjs';
import SideDrawer from '../../../_custom/SideDrawer/SideDrawer';
import styles from './AuditLogDrawer.module.scss';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';
import { AuditLogEvent } from '../../types';
import { resolveAuditEventColour } from '../../constants';
import { calculateTimeSpent } from '../../../../utils/dateUtils';
import ClockIcon from '../../../../assets/Svg/ClockIcon';

interface AuditLogDrawerProps {
    open: boolean;
    onClose: () => void;
    documentName: string;
    companyName?: string;
    events: AuditLogEvent[];
    loading: boolean;
}

interface AuditTimelineEntryProps {
    event: AuditLogEvent;
    /** The event immediately after this one in the timeline — undefined for the last entry. */
    nextEvent?: AuditLogEvent;
}

/**
 * A single row in the audit timeline: status pill + time-spent on the left, the
 * formatted timestamp/actor and IP/geolocation meta on the right.
 * Promoted out of AuditLogDrawer's render body so it's a named, testable subcomponent
 * (colocated-subcomponents convention) instead of a closure re-created on every render.
 */
const AuditTimelineEntry: React.FC<AuditTimelineEntryProps> = ({ event, nextEvent }) => {
    const timeSpent = calculateTimeSpent(event.timestamp, nextEvent?.timestamp);
    const formattedTime = dayjs(event.timestamp).format('h:mm a, D MMM YYYY');
    const c = resolveAuditEventColour(event.event);

    return (
        <div className={styles.TimelineEntry}>
            <div className={styles.TimelineLeft}>
                <div className={styles.BadgeRow}>
                    <div className={styles.StatusBadge} style={{ color: c.color, backgroundColor: c.bg }}>
                        {event.event}
                    </div>
                    <div className={styles.TimeSpent}>
                        <ClockIcon width="14" height="14" />
                        {timeSpent}
                    </div>
                </div>
            </div>

            <div className={styles.TimelineRight}>
                <div className={styles.StatusTime}>
                    {formattedTime}
                    {event.actorName ? ` by ${event.actorName}` : ''}
                </div>
                {(event.ip || (event.latitude != null && event.longitude != null)) && (
                    <span className={styles.EventMeta}>
                        {event.ip ? `IP: ${event.ip}` : ''}
                        {event.ip && event.latitude != null && event.longitude != null ? '  \u2022  ' : ''}
                        {event.latitude != null && event.longitude != null
                            ? `Location: ${event.latitude.toFixed(5)}, ${event.longitude.toFixed(5)}`
                            : ''}
                    </span>
                )}
            </div>
        </div>
    );
};

const AuditLogDrawer: React.FC<AuditLogDrawerProps> = ({ open, onClose, documentName, companyName, events, loading }) => {
    if (!open) return null;

    return (
        <SideDrawer width="40%" onClose={onClose}>
            <div className={styles.DrawerHeader}>
                <h2 className={styles.DrawerTitle}>Document Sign</h2>
                <p className={styles.CompanyName}>{companyName ?? documentName}</p>
            </div>

            <div className={styles.DrawerContent}>
                {companyName && <span className={styles.DocumentName}>{documentName}</span>}
                {loading ? (
                    <div className={styles.LoadingState}>
                        <LoadingSpinner height="2.5rem" color="var(--primary-text-colour)" />
                    </div>
                ) : events.length === 0 ? (
                    <div className={styles.EmptyState}>No audit events yet.</div>
                ) : (
                    <div className={styles.Timeline}>
                        {events.map((event, index) => (
                            <AuditTimelineEntry key={`${event.timestamp}-${index}`} event={event} nextEvent={events[index + 1]} />
                        ))}
                    </div>
                )}
            </div>
        </SideDrawer>
    );
};

export default AuditLogDrawer;
